import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Lock, ShoppingCart, Music, Video, Pencil, Save } from 'lucide-react';
import { usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Step 2 — Solo Play Along grid.
 *
 * Renders one card per Step-1 instrument. Card is either:
 *   - unlocked (owned via Master Pass / Master Pack / lesson / member) — plays audio + shows video tempos
 *   - locked — shows padlock + "Buy Master Pack" CTA
 *
 * Admins see a small "Edit URLs" panel per card that writes the audio + slow/med/fast video URLs
 * back to /api/backing-tracks.
 */
export default function BackingTracksGrid({ access, instruments, onBuyPack }) {
  const { isAdminUser, enabled } = usePageEditMode();
  const adminEditing = isAdminUser && enabled;
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTempoModal, setOpenTempoModal] = useState(null);
  const [editingInstrumentId, setEditingInstrumentId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const authHeaders = () => {
    const t = localStorage.getItem('auth_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadTracks = async () => {
    try {
      // Admin gets the full list (including unpublished), everyone else only published
      const url = isAdminUser ? `${API}/api/backing-tracks/all` : `${API}/api/backing-tracks`;
      const res = await axios.get(url, { headers: isAdminUser ? authHeaders() : {} });
      setTracks(res.data || []);
    } catch (_) {
      setTracks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTracks(); /* eslint-disable-next-line */ }, [isAdminUser]);

  const isUnlocked = (instrumentId) => {
    if (!access) return false;
    if (access.unlocks_all) return true;
    if ((access.master_pack_instruments || []).includes(instrumentId)) return true;
    if ((access.owned_lesson_ids || []).includes(instrumentId)) return true;
    return false;
  };

  // Show a card for every Step-1 instrument. If a track exists for the instrument, use it; else render a placeholder.
  const items = (instruments || []).map(inst => {
    const track = tracks.find(t => t.instrument_id === inst.id);
    return { instrument: inst, track };
  });

  if (loading) {
    return <div className="pl-0 md:pl-16 text-muted-foreground text-sm">Loading backing tracks…</div>;
  }

  if (items.length === 0) {
    return (
      <div className="pl-0 md:pl-16">
        <div className="bg-stone-800/40 border border-stone-700 rounded-2xl p-8 text-center">
          <Music className="w-12 h-12 mx-auto text-primary/60 mb-3" />
          <PageEditable path="step2Grid.emptyTitle" as="h3" className="text-lg font-bold text-white mb-2" fallback="Backing tracks land here" />
          <PageEditable path="step2Grid.emptyDescription" as="p" multiline className="text-sm text-muted-foreground" fallback="Add an instrument in Step 1 and its play-along track will appear here." />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="pl-0 md:pl-16 grid md:grid-cols-2 gap-4">
        {items.map(({ instrument, track }) => {
          const unlocked = isUnlocked(instrument.id);
          return (
            <div key={instrument.id} className="bg-stone-800/50 border border-stone-700 rounded-2xl overflow-hidden hover:border-primary/40 transition-colors">
              <div className="flex gap-4 p-5">
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-stone-900 relative group">
                  {instrument.image ? (
                    <img loading="lazy" decoding="async" src={instrument.image} alt={instrument.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-8 h-8 text-primary/50" />
                    </div>
                  )}
                  {adminEditing && (
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                      <span className="text-[10px] font-bold text-yellow-300 text-center">Edit in Step 1 lesson</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-white truncate">{instrument.name}</h4>
                  {track ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {track.bpm} BPM · {track.time_signature || '4/4'}{track.description ? ` · ${track.description}` : ''}
                    </p>
                  ) : (
                    <PageEditable path="step2Grid.trackComingSoon" as="p" className="text-xs text-muted-foreground mt-0.5" fallback="Backing track coming soon" />
                  )}
                </div>
                {unlocked ? (
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-primary/20 text-primary rounded-full px-2 py-1 self-start">
                    <PageEditable path="step2Grid.unlockedPill" fallback="Unlocked" />
                  </span>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-red-500/20 text-red-400 rounded-full px-2 py-1 self-start flex items-center gap-1">
                    <Lock className="w-3 h-3" /> <PageEditable path="step2Grid.lockedPill" fallback="Locked" />
                  </span>
                )}
              </div>

              {/* Body */}
              <div className="border-t border-stone-700/50 p-4 bg-stone-900/40">
                {adminEditing && editingInstrumentId === instrument.id ? (
                  <div className="space-y-2 text-xs">
                    <label className="block">
                      <span className="text-muted-foreground">Audio loop URL</span>
                      <input value={editDraft.audio_url || ''} onChange={(e)=>setEditDraft(d=>({...d, audio_url: e.target.value}))} className="w-full mt-1 px-2 py-1.5 bg-stone-800 border border-stone-700 rounded" placeholder="https://…" />
                    </label>
                    {['slow','medium','fast'].map(t => (
                      <label key={t} className="block">
                        <span className="text-muted-foreground capitalize">{t} tempo video</span>
                        <input value={editDraft[`video_${t}_url`] || ''} onChange={(e)=>setEditDraft(d=>({...d, [`video_${t}_url`]: e.target.value}))} className="w-full mt-1 px-2 py-1.5 bg-stone-800 border border-stone-700 rounded" placeholder="Vimeo / YouTube / MP4" />
                      </label>
                    ))}
                    <div className="grid grid-cols-2 gap-2">
                      <label>
                        <span className="text-muted-foreground">BPM</span>
                        <input type="number" value={editDraft.bpm || 100} onChange={(e)=>setEditDraft(d=>({...d, bpm: parseInt(e.target.value||'0',10)}))} className="w-full mt-1 px-2 py-1.5 bg-stone-800 border border-stone-700 rounded" />
                      </label>
                      <label>
                        <span className="text-muted-foreground">Time sig</span>
                        <input value={editDraft.time_signature || '4/4'} onChange={(e)=>setEditDraft(d=>({...d, time_signature: e.target.value}))} className="w-full mt-1 px-2 py-1.5 bg-stone-800 border border-stone-700 rounded" />
                      </label>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button onClick={()=>{ setEditingInstrumentId(null); setEditDraft({}); }} className="px-3 py-1.5 text-xs text-gray-300 hover:text-white">Cancel</button>
                      <button
                        onClick={async () => {
                          try {
                            const payload = {
                              instrument_id: instrument.id,
                              instrument_name: instrument.name,
                              audio_url: editDraft.audio_url || '',
                              video_slow_url: editDraft.video_slow_url || '',
                              video_medium_url: editDraft.video_medium_url || '',
                              video_fast_url: editDraft.video_fast_url || '',
                              bpm: editDraft.bpm || 100,
                              time_signature: editDraft.time_signature || '4/4',
                              description: editDraft.description || '',
                              is_published: editDraft.is_published !== false,
                            };
                            if (track?.id) {
                              await axios.patch(`${API}/api/backing-tracks/${track.id}`, payload, { headers: authHeaders() });
                            } else {
                              await axios.post(`${API}/api/backing-tracks`, payload, { headers: authHeaders() });
                            }
                            toast.success(`Saved backing track for ${instrument.name}`);
                            setEditingInstrumentId(null);
                            setEditDraft({});
                            await loadTracks();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Save failed');
                          }
                        }}
                        className="px-3 py-1.5 text-xs bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded inline-flex items-center gap-1"
                      >
                        <Save className="w-3 h-3" /> Save
                      </button>
                    </div>
                  </div>
                ) : unlocked ? (
                  <div className="flex flex-wrap gap-2 items-center">
                    {track?.audio_url ? (
                      <a
                        href={track.audio_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-full text-xs font-semibold"
                      >
                        <Play className="w-3 h-3" /> <PageEditable path="step2Grid.audioLoopLabel" fallback="Audio loop" />
                      </a>
                    ) : null}
                    {['slow', 'medium', 'fast'].map(tempo => {
                      const url = track?.[`video_${tempo}_url`];
                      if (!url) return null;
                      return (
                        <button
                          key={tempo}
                          onClick={() => setOpenTempoModal({ track, tempo, url })}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-stone-800 hover:bg-stone-700 text-white rounded-full text-xs font-semibold capitalize"
                        >
                          <Video className="w-3 h-3" /> <PageEditable path={`step2Grid.tempo.${tempo}`} fallback={tempo} />
                        </button>
                      );
                    })}
                    {!track && (
                      <PageEditable path="step2Grid.noTrackFallback" as="p" multiline className="text-xs text-muted-foreground italic" fallback="Master hasn't uploaded a backing track for this instrument yet." />
                    )}
                    {adminEditing && (
                      <button
                        onClick={() => { setEditingInstrumentId(instrument.id); setEditDraft(track ? { ...track } : {}); }}
                        className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-yellow-400 hover:text-yellow-300 border border-yellow-400/40 rounded-full"
                        data-testid={`edit-track-${instrument.id}`}
                      >
                        <Pencil className="w-3 h-3" /> Edit URLs
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => onBuyPack(instrument)}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-primary hover:bg-primary/90 text-black rounded-full text-sm font-bold"
                      data-testid={`buy-pack-${instrument.id}`}
                    >
                      <ShoppingCart className="w-4 h-4" /> <PageEditable path="step2Grid.unlockCta" fallback="Unlock — Master Pack $79" />
                    </button>
                    {adminEditing && (
                      <button
                        onClick={() => { setEditingInstrumentId(instrument.id); setEditDraft(track ? { ...track } : {}); }}
                        className="w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-semibold text-yellow-400 hover:text-yellow-300 border border-yellow-400/40 rounded-full"
                        data-testid={`edit-track-locked-${instrument.id}`}
                      >
                        <Pencil className="w-3 h-3" /> Edit URLs (admin)
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tempo Video Modal */}
      {openTempoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setOpenTempoModal(null)}
        >
          <div
            className="bg-black w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-stone-900">
              <h3 className="text-white font-semibold capitalize">
                {openTempoModal.track.instrument_name} — {openTempoModal.tempo}
              </h3>
              <button onClick={() => setOpenTempoModal(null)} className="text-zinc-400 hover:text-white">
                Close
              </button>
            </div>
            <div className="aspect-video bg-black">
              {(() => {
                const url = openTempoModal.url;
                const vimeo = url?.match(/vimeo\.com\/(\d+)/);
                const yt = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
                if (vimeo) return (
                  <iframe src={`https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`} className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="Tempo" />
                );
                if (yt) return (
                  <iframe src={`https://www.youtube.com/embed/${yt[1]}?autoplay=1`} className="w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen title="Tempo" />
                );
                return <video src={url} className="w-full h-full" controls autoPlay />;
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
